import {z} from "zod";
import {mongoObjectIdSchema} from "./mongo.ts";

export {mongoObjectIdSchema} from "./mongo.ts";

/** IANA timezone used for OTK business-calendar operations. */
export const OTK_TIME_ZONE = "Europe/Kyiv";

/** OTK-specific authorization data stored on a Better Auth user document. */
export const Roles = {
    ADMIN: "ADMIN",
    RESPONSIBLE_PERSON: "RESPONSIBLE_PERSON",
    OPERATOR: "OPERATOR",
} as const;

export type Role = typeof Roles[keyof typeof Roles];

export const roleSchema = z.enum(Roles);

export const RoleLabels: Record<Role, string> = {
    [Roles.ADMIN]: "Адмін",
    [Roles.RESPONSIBLE_PERSON]: "Відповідальна особа суб'єкта ОТК",
    [Roles.OPERATOR]: "Оператор ОТК",
};

export const Permissions = {
    VIEW_ASSIGNED_PUNKTY: "viewAssignedPunkty",
    MANAGE_DRAFTS: "manageDrafts",
    MANAGE_USERS_ROLES_INVITES: "manageUsersRolesInvites",
    ALL_PUNKTY_STATS: "allPunktyStats",
    EDRTZ_KEP_SERVICES: "edrtzKepServices",
    SIGN_AND_SEND_EDRTZ: "signAndSendEdrtz",
    MANAGE_WAREHOUSE_DISTRIBUTION: "manageWarehouseDistribution",
    MANAGE_SCHEDULE: "manageSchedule",
    VIEW_PAYMENTS: "viewPayments",
    MANAGE_PAYMENTS: "managePayments",
    VIEW_AUDIT: "viewAudit",
} as const;

export type Permission = typeof Permissions[keyof typeof Permissions];

export const PermissionValues = Object.freeze(Object.values(Permissions)) as readonly Permission[];

export const rolesSchema = z.array(roleSchema);

export const otkAuthenticatedUserSchema = z.looseObject({
    id: z.string(),
    subjectId: mongoObjectIdSchema.nullish(),
    roles: rolesSchema.nullish(),
});
export type OtkAuthenticatedUser = z.infer<typeof otkAuthenticatedUserSchema>;

export const ROLE_PERMISSIONS = {
    [Roles.ADMIN]: [
        Permissions.VIEW_ASSIGNED_PUNKTY,
        Permissions.MANAGE_USERS_ROLES_INVITES,
        Permissions.ALL_PUNKTY_STATS,
        Permissions.EDRTZ_KEP_SERVICES,
        Permissions.SIGN_AND_SEND_EDRTZ,
        Permissions.MANAGE_WAREHOUSE_DISTRIBUTION,
        Permissions.VIEW_PAYMENTS,
        Permissions.MANAGE_PAYMENTS,
        Permissions.VIEW_AUDIT,
    ],
    [Roles.RESPONSIBLE_PERSON]: [
        Permissions.VIEW_ASSIGNED_PUNKTY,
        Permissions.MANAGE_DRAFTS,
        Permissions.EDRTZ_KEP_SERVICES,
        Permissions.SIGN_AND_SEND_EDRTZ,
        Permissions.MANAGE_SCHEDULE,
        Permissions.MANAGE_WAREHOUSE_DISTRIBUTION,
    ],
    [Roles.OPERATOR]: [
        Permissions.VIEW_ASSIGNED_PUNKTY,
        Permissions.MANAGE_DRAFTS,
    ],
} as const satisfies Record<Role, readonly Permission[]>;

export function getPermissionsByRole(role: Role): readonly Permission[] {
    return ROLE_PERMISSIONS[role];
}

/** login.md endpoint 4 / vidpovidalna-osoba.md's `посада` wire enum. */
export type Posada = "КЕРІВНИК" | "ЗАСТУПНИК_КЕРІВНИКА" | "ВІДПОВІДАЛЬНА_ОСОБА";

export type SubjectStatus = "АКТИВНИЙ" | "ВИКЛЮЧЕНИЙ";
export type PermitStatus = "ДІЄ" | "ПРИЗУПИНЕНО" | "ВІДКЛИКАНО";

// ---- otk-be routes/otk-subjects.ts response types ----

export interface KepMatch {
    certOwner: string;
    certSerial: string;
    posada: Posada;
}

/** The `subject` summary nested in `POST /otk/subjects/resolve-by-kep`'s response. */
export interface ResolveByKepSubject {
    name: string;
    edrpou: string;
    address: string;
    status: string;
}

export interface ResolveByKepResult {
    match: KepMatch | null;
    subject?: ResolveByKepSubject;
}

export interface OtkSubjectPermit {
    nomerDozvolu: string;
    dataVydachi: string;
    status: PermitStatus;
    kategorii: string[];
}

export interface OtkSubjectResponsiblePerson {
    rnokpp: string;
    certificateSerial: string;
    prizvyshche: string;
    imia: string;
    poBatkovi?: string;
    posada: Posada;
}

/** `GET /otk/subject`'s response. */
export interface OtkSubjectProfile {
    id: string;
    kodVReiestri: string;
    nazva: string;
    edrpou: string;
    status: SubjectStatus;
    rscId: string;
    oblast: string;
    mistoNaselenyiPunkt: string;
    vulytsia: string;
    budynok: string;
    permit: OtkSubjectPermit;
    responsiblePersons: OtkSubjectResponsiblePerson[];
    branches: OtkBranch[];
}

// ---- otk-be routes/otk-users.ts response types ----

/** `GET /otk/users`, and the `add-role`/`remove-role` mutation responses. */
export interface OtkUser {
    id: string;
    name: string;
    email: string;
    subjectId: string;
    rnokpp: string;
    roles: Role[];
}

export interface OtkBranch {
    registryId: string;
    number: string;
    name: string;
    punktManager: string;
    region: string;
    city: string;
    street: string;
    building: string;
    status: BranchStatus
}

export type BranchStatus = "ACTIVE" | "SUSPENDED" | "CLOSED"

/** `GET /otk/roles`'s response. */
export interface OtkRole {
    id: Role;
    name: string;
    permissions: Record<Permission, boolean>;
}

export * from "./otk-document.ts";
export * from "./otk-blank.ts";
export * from "./otk-destruction-act.ts";
export * from "./otk-print-profile.ts";
